import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Check, Zap, Clock } from "lucide-react";

interface Problem {
  question: string;
  answer: number;
  technique: string;
  explanation: string;
}

interface InteractiveDemoWidgetProps {
  onVideoPlayback?: (shouldPlay: boolean) => void;
}

const DEMO_PROBLEMS: Problem[] = [
  {
    question: "24682 × 5",
    answer: 123410,
    technique: "Multiply by 5 Shortcut",
    explanation:
      "To multiply by 5: Multiply by 10 (add a zero) → 246820, then divide by 2 → 123410",
  },
  {
    question: "24 × 11",
    answer: 264,
    technique: "Multiply by 11 Trick",
    explanation:
      "To multiply by 11: Split the number (2_4), add the digits (2+4=6), put in middle → 264",
  },
  {
    question: "15% of 50",
    answer: 7.5,
    technique: "Percentage Shortcuts (2 Methods)",
    explanation:
      "Method 1: Calculate 10% (÷10 = 5) + 5% (÷2 = 2.5) → 5 + 2.5 = 7.5. Method 2 (Easier!): 15% of 50 = 50% of 15 → 15 ÷ 2 = 7.5",
  },
];

export default function InteractiveDemoWidget({ onVideoPlayback }: InteractiveDemoWidgetProps) {
  const [currentProblem, setCurrentProblem] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timer, setTimer] = useState(0);
  const [firstAttempt, setFirstAttempt] = useState<{
    time: number;
    answer: string;
    correct: boolean;
  } | null>(null);
  const [secondAttempt, setSecondAttempt] = useState<{
    time: number;
    answer: string;
    correct: boolean;
  } | null>(null);
  const [showTechnique, setShowTechnique] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [secondAttemptError, setSecondAttemptError] = useState(false);

  const problem = DEMO_PROBLEMS[currentProblem];

  useEffect(() => {
    if (isTimerRunning) {
      const interval = setInterval(() => setTimer((t) => t + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [isTimerRunning]);

  const handleStartChallenge = () => {
    setIsTimerRunning(true);
    setTimer(0);
    setUserAnswer("");
    onVideoPlayback?.(false); // Pause video when timer starts
  };

  const handleSubmit = () => {
    const answer = parseFloat(userAnswer);
    const correct = Math.abs(answer - problem.answer) < 0.01;

    if (!firstAttempt) {
      // First attempt - accept any answer
      setIsTimerRunning(false);
      setFirstAttempt({ time: timer, answer: userAnswer, correct });
      setShowTechnique(true);
      onVideoPlayback?.(true); // Resume video when timer stops
    } else {
      // Second attempt (with technique) - must be correct
      if (correct) {
        setIsTimerRunning(false);
        setSecondAttempt({ time: timer, answer: userAnswer, correct: true });
        setSecondAttemptError(false);
        onVideoPlayback?.(true); // Resume video when timer stops
      } else {
        // Wrong answer on second attempt - show error, keep technique visible
        setIsTimerRunning(false);
        setSecondAttemptError(true);
        onVideoPlayback?.(true); // Resume video when timer stops
      }
    }
  };

  const handleTryAgainWithTechnique = () => {
    setUserAnswer("");
    setIsTimerRunning(true);
    setTimer(0);
    setSecondAttemptError(false);
    onVideoPlayback?.(false); // Pause video when timer starts again
  };

  const handleNextProblem = () => {
    if (currentProblem < DEMO_PROBLEMS.length - 1) {
      setCurrentProblem(currentProblem + 1);
      setUserAnswer("");
      setFirstAttempt(null);
      setSecondAttempt(null);
      setShowTechnique(false);
      setIsTimerRunning(false);
      setTimer(0);
    } else {
      setCompleted(true);
    }
  };

  const handleSkipToCompletion = () => {
    setCompleted(true);
  };

  const handleLaunchApp = () => {
    if (
      window.location.hostname === "ithopu.com" ||
      window.location.hostname === "www.ithopu.com"
    ) {
      window.location.href = "https://app.ithopu.com";
    } else {
      window.location.href = "/dashboard";
    }
  };

  // Completion screen - after all challenges
  if (completed) {
    return (
      <Card className="border-4 border-green-500 dark:border-green-400 bg-gradient-to-br from-green-50 to-white dark:from-green-950 dark:to-zinc-900 shadow-2xl">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-3xl font-bold mb-4 text-zinc-900 dark:text-white">
            You Just Experienced Mental Math Shortcuts! 🎉
          </h3>
          <p className="text-xl text-zinc-600 dark:text-zinc-400 mb-6 max-w-2xl mx-auto">
            But knowing the trick is only <span className="font-bold text-green-600">20%</span> of mastery.
          </p>
          <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 mb-6 border-2 border-zinc-200 dark:border-zinc-700">
            <p className="text-lg text-zinc-700 dark:text-zinc-300 mb-4">
              The other <span className="font-bold text-blue-600">80%</span>?
            </p>
            <ul className="text-left space-y-3 max-w-lg mx-auto">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700 dark:text-zinc-300">
                  <strong>1000+ practice problems</strong> with adaptive difficulty
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700 dark:text-zinc-300">
                  <strong>Battle mode</strong> - compete with friends
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700 dark:text-zinc-300">
                  <strong>Daily challenges</strong> - build consistency
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700 dark:text-zinc-300">
                  <strong>Progress tracking</strong> - see your growth
                </span>
              </li>
            </ul>
          </div>
          <Button size="lg" onClick={handleLaunchApp} className="text-lg px-8 py-6">
            Start Free Trial - Master 90+ Techniques
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-4">
            No credit card required • Start in 30 seconds
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-4 border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-zinc-900 shadow-2xl">
      <CardContent className="pt-8 pb-8">
        <div className="inline-block px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-sm font-medium mb-6">
          Challenge {currentProblem + 1} of {DEMO_PROBLEMS.length}
        </div>

        {/* Initial state - show problem */}
        {!firstAttempt && !isTimerRunning && (
          <div className="text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-6 text-zinc-900 dark:text-white">
              Can you solve this quickly?
            </h3>
            <div className="text-5xl md:text-6xl font-bold text-blue-600 dark:text-blue-400 mb-8">
              {problem.question}
            </div>
            <Button size="lg" onClick={handleStartChallenge} className="text-lg px-8 py-6">
              Start Timer
              <Clock className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* First attempt - timer running */}
        {!firstAttempt && isTimerRunning && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-zinc-900 dark:text-white">
                {problem.question} = ?
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <span className="text-xl font-bold text-orange-700 dark:text-orange-300">
                  {timer}s
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && userAnswer) {
                    handleSubmit();
                  }
                }}
                placeholder="Your answer..."
                className="flex-1 px-4 py-3 text-xl border-2 border-zinc-300 dark:border-zinc-600 rounded-lg focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                autoFocus
              />
              <Button onClick={handleSubmit} disabled={!userAnswer} size="lg" className="px-6">
                Submit
              </Button>
            </div>
          </div>
        )}

        {/* After first attempt - show results and technique */}
        {firstAttempt && !secondAttempt && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className={`p-6 rounded-xl border-2 ${
              firstAttempt.correct
                ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                : "bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700"
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-lg font-bold text-zinc-900 dark:text-white">
                    {firstAttempt.correct ? "✅ Correct!" : "❌ Not quite"}
                  </p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    {firstAttempt.correct
                      ? `Your answer: ${firstAttempt.answer}`
                      : `Your answer: ${firstAttempt.answer} | Correct: ${problem.answer}`}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {firstAttempt.time}s
                  </div>
                  <div className="text-sm text-zinc-600 dark:text-zinc-400">Time taken</div>
                </div>
              </div>
            </div>

            {showTechnique && (
              <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 border-4 border-purple-400 dark:border-purple-600 rounded-xl shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  <h4 className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                    Here's the Mental Math Shortcut:
                  </h4>
                </div>
                <div className="bg-white dark:bg-zinc-800 rounded-lg p-6 mb-6 border-2 border-purple-200 dark:border-purple-700">
                  <div className="text-center mb-4">
                    <p className="text-lg text-purple-600 dark:text-purple-400 font-semibold mb-2">
                      {problem.technique}
                    </p>
                    <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                      {problem.question}
                    </div>
                  </div>
                  <div className="border-t-2 border-purple-200 dark:border-purple-700 pt-4 mt-4">
                    {currentProblem === 0 && (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-600 dark:bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                            1
                          </div>
                          <div>
                            <p className="text-xl font-bold text-zinc-900 dark:text-white">
                              Multiply by 10
                            </p>
                            <p className="text-lg text-zinc-600 dark:text-zinc-400">
                              24682 × 10 = <span className="font-semibold">246820</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-600 dark:bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                            2
                          </div>
                          <div>
                            <p className="text-xl font-bold text-zinc-900 dark:text-white">
                              Divide by 2
                            </p>
                            <p className="text-lg text-zinc-600 dark:text-zinc-400">
                              246820 ÷ 2 = <span className="font-semibold text-green-600 dark:text-green-400 text-2xl">123410</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {currentProblem === 1 && (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-600 dark:bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                            1
                          </div>
                          <div>
                            <p className="text-xl font-bold text-zinc-900 dark:text-white">
                              Split the number
                            </p>
                            <p className="text-lg text-zinc-600 dark:text-zinc-400">
                              24 → 2 <span className="text-purple-600 dark:text-purple-400">_</span> 4
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-600 dark:bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                            2
                          </div>
                          <div>
                            <p className="text-xl font-bold text-zinc-900 dark:text-white">
                              Add the digits
                            </p>
                            <p className="text-lg text-zinc-600 dark:text-zinc-400">
                              2 + 4 = <span className="font-semibold">6</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-600 dark:bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                            3
                          </div>
                          <div>
                            <p className="text-xl font-bold text-zinc-900 dark:text-white">
                              Put in the middle
                            </p>
                            <p className="text-lg text-zinc-600 dark:text-zinc-400">
                              2 <span className="text-purple-600 dark:text-purple-400 font-bold">6</span> 4 = <span className="font-semibold text-green-600 dark:text-green-400 text-2xl">264</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {currentProblem === 2 && (
                      <div className="space-y-4">
                        {/* Method 1 - Traditional */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-700">
                          <p className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">
                            📘 Method 1: Traditional Way
                          </p>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                                1
                              </div>
                              <div>
                                <p className="text-lg font-bold text-zinc-900 dark:text-white">
                                  Calculate 10%
                                </p>
                                <p className="text-base text-zinc-600 dark:text-zinc-400">
                                  50 ÷ 10 = <span className="font-semibold">5</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                                2
                              </div>
                              <div>
                                <p className="text-lg font-bold text-zinc-900 dark:text-white">
                                  Calculate 5%
                                </p>
                                <p className="text-base text-zinc-600 dark:text-zinc-400">
                                  5 ÷ 2 = <span className="font-semibold">2.5</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                                3
                              </div>
                              <div>
                                <p className="text-lg font-bold text-zinc-900 dark:text-white">
                                  Add them
                                </p>
                                <p className="text-base text-zinc-600 dark:text-zinc-400">
                                  5 + 2.5 = <span className="font-semibold">7.5</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Method 2 - Reverse Percentage (Easier!) */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border-2 border-green-300 dark:border-green-700">
                          <div className="flex items-center gap-2 mb-3">
                            <p className="text-lg font-bold text-green-900 dark:text-green-100">
                              ⚡ Method 2: Reverse Percentage
                            </p>
                            <span className="px-2 py-1 bg-yellow-400 dark:bg-yellow-600 text-yellow-900 dark:text-yellow-100 text-xs font-bold rounded">
                              EASIER!
                            </span>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-green-600 dark:bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                                1
                              </div>
                              <div>
                                <p className="text-lg font-bold text-zinc-900 dark:text-white">
                                  Flip the percentage
                                </p>
                                <p className="text-base text-zinc-600 dark:text-zinc-400">
                                  15% of 50 = <span className="font-semibold text-green-700 dark:text-green-300">50% of 15</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-green-600 dark:bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                                2
                              </div>
                              <div>
                                <p className="text-lg font-bold text-zinc-900 dark:text-white">
                                  Calculate 50% (just divide by 2!)
                                </p>
                                <p className="text-base text-zinc-600 dark:text-zinc-400">
                                  15 ÷ 2 = <span className="font-semibold text-green-600 dark:text-green-400 text-2xl">7.5</span>
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 p-3 bg-green-100 dark:bg-green-900/30 rounded border-l-4 border-green-600">
                            <p className="text-sm font-medium text-green-900 dark:text-green-100">
                              💡 <strong>Why this works:</strong> Percentage multiplication is commutative!
                              15% × 50 = 50% × 15
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {!isTimerRunning && (
                  <Button
                    onClick={handleTryAgainWithTechnique}
                    size="lg"
                    className="w-full text-lg py-6"
                  >
                    Try Again With This Technique
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                )}
              </div>
            )}

            {/* Second attempt - timer running */}
            {isTimerRunning && (
              <div className="space-y-4">
                {/* Show the question and technique while solving */}
                <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-lg border-2 border-purple-300 dark:border-purple-700">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-purple-700 dark:text-purple-300 font-semibold mb-1">
                        {problem.technique}
                      </p>
                      <div className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                        {problem.question}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      <span className="text-xl font-bold text-orange-700 dark:text-orange-300">
                        {timer}s
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                    💡 Use the technique above to solve faster!
                  </div>
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && userAnswer) {
                        handleSubmit();
                      }
                    }}
                    placeholder="Your answer..."
                    className="flex-1 px-4 py-3 text-xl border-2 border-zinc-300 dark:border-zinc-600 rounded-lg focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
                    autoFocus
                  />
                  <Button onClick={handleSubmit} disabled={!userAnswer} size="lg" className="px-6">
                    Submit
                  </Button>
                </div>
              </div>
            )}

            {/* Second attempt error - wrong answer */}
            {secondAttemptError && !isTimerRunning && (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="p-6 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="text-3xl">❌</div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-red-900 dark:text-red-100 mb-2">
                        Not quite! Let's try again using the technique.
                      </p>
                      <p className="text-red-700 dark:text-red-300">
                        Your answer: <span className="font-semibold">{userAnswer}</span> |
                        Correct answer: <span className="font-semibold">{problem.answer}</span>
                      </p>
                    </div>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded">
                    <p className="text-sm text-yellow-900 dark:text-yellow-100 font-medium">
                      💡 Review the steps above carefully and try solving it step-by-step using the technique!
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setUserAnswer("");
                    setSecondAttemptError(false);
                    setIsTimerRunning(true);
                    setTimer(0);
                  }}
                  size="lg"
                  className="w-full"
                >
                  Try Again
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* After second attempt - show comparison */}
        {secondAttempt && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-2 border-green-300 dark:border-green-700 rounded-xl">
              <h4 className="text-2xl font-bold mb-6 text-center text-zinc-900 dark:text-white">
                Your Progress 🚀
              </h4>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="text-center p-4 bg-white dark:bg-zinc-800 rounded-lg">
                  <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                    First Attempt
                  </div>
                  <div className="text-4xl font-bold text-red-600 dark:text-red-400">
                    {firstAttempt?.time}s
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    {firstAttempt?.correct ? "Correct" : "Incorrect"}
                  </div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-zinc-800 rounded-lg">
                  <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                    With Technique
                  </div>
                  <div className="text-4xl font-bold text-green-600 dark:text-green-400">
                    {secondAttempt.time}s
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    {secondAttempt.correct ? "Correct" : "Incorrect"}
                  </div>
                </div>
              </div>
              {secondAttempt.time < (firstAttempt?.time ?? Infinity) && (
                <div className="text-center p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <p className="text-lg font-bold text-yellow-900 dark:text-yellow-100">
                    🎉 {((firstAttempt?.time ?? 0) / secondAttempt.time).toFixed(1)}x faster with the technique!
                  </p>
                </div>
              )}
            </div>

            <div className="text-center space-y-4">
              <p className="text-lg text-zinc-700 dark:text-zinc-300 font-medium">
                Want to try another challenge?
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={handleNextProblem} size="lg" className="px-8">
                  {currentProblem < DEMO_PROBLEMS.length - 1
                    ? "Yes - Next Challenge"
                    : "Yes - One More"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={handleSkipToCompletion}
                  size="lg"
                  variant="outline"
                  className="px-8"
                >
                  No - Show Me the Platform
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
